import { inject, Injectable } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { Observable } from 'rxjs';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.component';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ConfirmationDialogService {
  private readonly dialog = inject(Dialog);

  /**
   * Emits:
   * - `true` if user clicked the affirmative button
   * - `false` if user clicked the negative button
   * - `undefined` if user dismissed (e.g. header "X", ESC, backdrop)
   */
  openConfirmationDialog(dialogConfig?: Partial<ConfirmationDialogData>): Observable<boolean | undefined> {
    const dialogRef = this.dialog.open<boolean>(ConfirmationDialogComponent, {
      data: { ...dialogConfig },
      ...TsDialogComponent.getDefaultDialogConfig(),
    });

    return dialogRef.closed;
  }

  static deleteConfirmationCommonLayoutConfig(
    objectName: string,
    objectTypeLabel?: string,
  ): Partial<ConfirmationDialogData> {
    const formattedName = objectTypeLabel ? `${objectTypeLabel} - ${objectName}` : objectName;
    return {
      dialogTitle: 'Confirm Deletion',
      description: `You are deleting <b>${formattedName}</b>. This action is not reversible.`,
      renderHtml: true,
      buttonsConfig: {
        yes: 'Delete',
        no: 'Cancel',
      },
    };
  }

  static unsavedChangesCommonLayoutConfig(): Partial<ConfirmationDialogData> {
    return {
      dialogTitle: 'Discard Changes?',
      description: 'Changes haven’t been saved. Do you wish to discard the changes?',
      renderHtml: false,
      buttonsConfig: {
        yes: 'Discard',
        no: 'No',
      },
    };
  }

  static updateGeoCodeCommonLayoutConfig(customText?: { entityName?: string }): Partial<ConfirmationDialogData> {
    return {
      dialogTitle: 'Update Address Location',
      description: `
        Would you like us to also update
        ${customText?.entityName ? `property <strong>${customText.entityName}</strong> address` : 'the address'}
        location based on your changes?
      `,
      renderHtml: true,
      buttonsConfig: {
        yes: 'Update',
        no: 'Skip',
      },
    };
  }
}
